import type { MetricCardTrend } from "../shared/MetricCard";
import type { ReviewsSummary } from "./types";

export type ReviewsKpiDelta = {
  delta: string;
  trend: MetricCardTrend;
};

export type ReviewsKpiDeltas = {
  overallRating: ReviewsKpiDelta;
  positiveNeutral: ReviewsKpiDelta;
  negative: ReviewsKpiDelta;
  reviewsCount: ReviewsKpiDelta;
};

const NEUTRAL_DELTA: ReviewsKpiDelta = {
  delta: "—",
  trend: "neutral",
};

function formatSignedPercentChange(current: number, previous: number): string {
  if (previous === 0) {
    return current === 0 ? "0%" : "—";
  }

  const change = ((current - previous) / previous) * 100;
  const rounded = Math.round(change * 10) / 10;
  const sign = rounded > 0 ? "+" : "";
  return `${sign}${rounded}%`;
}

function formatSignedPointChange(
  current: number,
  previous: number,
  suffix = "",
): string {
  const change = Math.round((current - previous) * 10) / 10;
  if (change === 0) {
    return `0${suffix}`;
  }

  const sign = change > 0 ? "+" : "";
  return `${sign}${change}${suffix}`;
}

function trendFromHigherIsBetter(change: number): MetricCardTrend {
  if (change > 0) {
    return "positive";
  }
  if (change < 0) {
    return "negative";
  }
  return "neutral";
}

function trendFromLowerIsBetter(change: number): MetricCardTrend {
  if (change < 0) {
    return "positive";
  }
  if (change > 0) {
    return "negative";
  }
  return "neutral";
}

export function computeReviewsKpiDeltas(
  current: ReviewsSummary,
  previous: ReviewsSummary | null,
): ReviewsKpiDeltas {
  if (!previous || previous.reviewsCount === 0) {
    return {
      overallRating: NEUTRAL_DELTA,
      positiveNeutral: NEUTRAL_DELTA,
      negative: NEUTRAL_DELTA,
      reviewsCount: NEUTRAL_DELTA,
    };
  }

  const overallRating =
    current.overallRating != null && previous.overallRating != null
      ? {
          delta: formatSignedPointChange(
            current.overallRating,
            previous.overallRating,
          ),
          trend: trendFromHigherIsBetter(
            current.overallRating - previous.overallRating,
          ),
        }
      : NEUTRAL_DELTA;

  const positiveNeutral =
    current.positiveNeutralPercent != null &&
    previous.positiveNeutralPercent != null
      ? {
          delta: formatSignedPointChange(
            current.positiveNeutralPercent,
            previous.positiveNeutralPercent,
            "%",
          ),
          trend: trendFromHigherIsBetter(
            current.positiveNeutralPercent - previous.positiveNeutralPercent,
          ),
        }
      : NEUTRAL_DELTA;

  const negative =
    current.negativePercent != null && previous.negativePercent != null
      ? {
          delta: formatSignedPointChange(
            current.negativePercent,
            previous.negativePercent,
            "%",
          ),
          trend: trendFromLowerIsBetter(
            current.negativePercent - previous.negativePercent,
          ),
        }
      : NEUTRAL_DELTA;

  const reviewsCount = {
    delta: formatSignedPercentChange(
      current.reviewsCount,
      previous.reviewsCount,
    ),
    trend: trendFromHigherIsBetter(
      current.reviewsCount - previous.reviewsCount,
    ),
  };

  return {
    overallRating,
    positiveNeutral,
    negative,
    reviewsCount,
  };
}
